import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';
import { Folder } from '@/users/components/UserFolders/types';

// Types
interface FoldersState {
  folders: Folder[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
}

const initialState: FoldersState = {
  folders: [],
  isLoading: false,
  error: null,
  hasFetched: false,
};

// Async Thunks
export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (category?: string) => {
    const url = category
      ? `/folders?category=${encodeURIComponent(category)}`
      : `/folders`;
    const response = await myFetch(url);
    return response.data || [];
  }
);

/** Loads a single folder into the store (used e.g. on edit-structure). */
export const fetchFolderById = createAsyncThunk(
  'folders/fetchFolderById',
  async (folderId: string) => {
    const response = await myFetch(`/folders/${encodeURIComponent(folderId)}`);
    return response.data;
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async ({
    folderId,
    updates,
  }: {
    folderId: string;
    updates: Partial<Folder>;
  }) => {
    await myFetch(`/folders/${folderId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return { folderId, updates };
  }
);

export const updateFolderFile = createAsyncThunk(
  'folders/updateFolderFile',
  async (
    {
      folderId,
      fileId,
      updates,
    }: {
      folderId: string;
      fileId: string;
      updates: any;
    },
    { rejectWithValue }
  ) => {
    try {
      await myFetch(`/folders/${folderId}/files/${fileId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      return { folderId, fileId, updates };
    } catch (error: any) {
      // Preserve error structure with status property
      return rejectWithValue({
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        body: error.body,
        retryable: error.retryable,
        details: error.details,
      });
    }
  }
);

export const deleteFolderFile = createAsyncThunk(
  'folders/deleteFolderFile',
  async ({ folderId, fileId }: { folderId: string; fileId: string }) => {
    await myFetch(`/folders/${folderId}/files/${fileId}`, {
      method: 'DELETE',
    });
    return { folderId, fileId };
  }
);

export const duplicateFolderFile = createAsyncThunk(
  'folders/duplicateFolderFile',
  async ({ folderId, fileId }: { folderId: string; fileId: string }) => {
    const response = await myFetch(
      `/folders/${folderId}/files/${fileId}/duplicate`,
      {
        method: 'POST',
      }
    );
    return { folderId, newFile: response.data };
  }
);

export const addFileToFolder = createAsyncThunk(
  'folders/addFileToFolder',
  async ({ folderName, fileData }: { folderName: string; fileData: any }) => {
    const response = await myFetch(`/folders/${folderName}/files`, {
      method: 'POST',
      body: JSON.stringify(fileData),
    });
    return { folderName, newFile: response.data };
  }
);

export const sendFileEmail = createAsyncThunk(
  'folders/sendFileEmail',
  async ({
    folderId,
    fileId,
    to,
  }: {
    folderId: string;
    fileId: string;
    to: string;
  }) => {
    const response = await myFetch(
      `/folders/${folderId}/files/${fileId}/send-email`,
      {
        method: 'POST',
        body: JSON.stringify({ to }),
      }
    );
    // response.data contains the full file object with updated emailsSent
    return { folderId, fileId, emailsSent: response.data.emailsSent || 0 };
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async ({ folderId }: { folderId: string }) => {
    await myFetch(`/folders/${folderId}`, {
      method: 'DELETE',
    });
    return { folderId };
  }
);

export const fetchAndAddFolder = createAsyncThunk(
  'folders/fetchAndAddFolder',
  async ({ folderName }: { folderName: string }) => {
    const response = await myFetch(`/folders/${folderName}`);
    return response.data;
  }
);

// Client folder operations (for folder_access tokens)
export const fetchClientFolder = createAsyncThunk(
  'folders/fetchClientFolder',
  async ({ folderName }: { folderName: string }, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/folders/${folderName}`);
      return response.data;
    } catch (error: any) {
      // Preserve error structure with status property
      return rejectWithValue({
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        body: error.body,
      });
    }
  }
);

export const updateClientFile = createAsyncThunk(
  'folders/updateClientFile',
  async (
    {
      folderId,
      fileId,
      updates,
    }: {
      folderId: string;
      fileId: string;
      updates: any;
    },
    { rejectWithValue }
  ) => {
    try {
      await myFetch(`/folders/${folderId}/files/${fileId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...updates,
          ...(updates.fields && {
            fields: updates.fields.map((field: any) => ({
              name: field.name,
              type: field.type,
              value: field.value,
            })),
          }),
        }),
      });
      return { folderId, fileId, updates };
    } catch (error: any) {
      // Preserve error structure with status property
      return rejectWithValue({
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        body: error.body,
        retryable: error.retryable,
        details: error.details,
      });
    }
  }
);

// Helper function to process infinite-list field values
// Handles isNew, isDeleted, and isEditing flags
const processInfiniteListValue = (
  existingValue: any[],
  incomingValue: any[]
): any[] => {
  if (!Array.isArray(existingValue) || !Array.isArray(incomingValue)) {
    return incomingValue || existingValue || [];
  }

  // Create a map of existing entries by id for quick lookup
  const existingEntriesMap = new Map<string, any>();
  existingValue.forEach((entry: any) => {
    if (entry && entry.id) {
      existingEntriesMap.set(entry.id, entry);
    }
  });

  // Process entries with flags
  const entriesToAdd: any[] = [];
  const entriesToUpdate = new Map<string, any>();
  const entriesToDelete = new Set<string>();
  const processedIds = new Set<string>();

  // First pass: identify entries to add, update, or delete
  incomingValue.forEach((entry: any) => {
    if (!entry || !entry.id) return;

    processedIds.add(entry.id);

    if (entry.isNew === true) {
      // Check if entry with this id already exists (shouldn't happen, but safety check)
      if (!existingEntriesMap.has(entry.id)) {
        // Add new entries (remove isNew flag)
        const { isNew, ...entryWithoutFlag } = entry;
        entriesToAdd.push(entryWithoutFlag);
      } else {
        // If it already exists, treat it as an update instead
        const { isNew, ...entryWithoutFlag } = entry;
        entriesToUpdate.set(entry.id, entryWithoutFlag);
      }
    } else if (entry.isDeleted === true) {
      // Mark for deletion
      entriesToDelete.add(entry.id);
    } else if (entry.isEditing === true) {
      // Mark for update (remove isEditing flag)
      const { isEditing, ...entryWithoutFlag } = entry;
      entriesToUpdate.set(entry.id, entryWithoutFlag);
    } else {
      // Regular update (no flag)
      entriesToUpdate.set(entry.id, entry);
    }
  });

  // Build result: keep existing entries, apply updates/deletes, add new entries
  const result: any[] = [];

  // Process existing entries
  existingValue.forEach((existingEntry: any) => {
    if (!existingEntry || !existingEntry.id) return;

    // Skip deleted entries
    if (entriesToDelete.has(existingEntry.id)) {
      return;
    }

    // Update if marked for update
    if (entriesToUpdate.has(existingEntry.id)) {
      const update = entriesToUpdate.get(existingEntry.id);
      result.push({ ...existingEntry, ...update });
      entriesToUpdate.delete(existingEntry.id); // Remove from map to track what's left
    } else if (!processedIds.has(existingEntry.id)) {
      // Keep existing entry as-is only if it wasn't in incoming (not processed)
      result.push(existingEntry);
    }
  });

  // Add any remaining entries from incoming that weren't in existing (updates without existing)
  entriesToUpdate.forEach((update) => {
    result.push(update);
  });

  // Add new entries (only those that don't already exist in result)
  // Use a Set to track ids already in result to prevent duplicates
  const resultIds = new Set(result.map((e) => e?.id).filter(Boolean));
  entriesToAdd.forEach((newEntry) => {
    if (!resultIds.has(newEntry.id)) {
      result.push(newEntry);
      resultIds.add(newEntry.id); // Track it to prevent duplicates
    }
  });

  return result;
};

// Helper function to apply side effects
// Matches backend behavior in fileProcessor.js
export const applySideEffects = (
  file: any,
  updates: any,
  hasFieldsUpdate: boolean
) => {
  // Side effect: when fields are updated, set status to 1 (takes precedence)
  // But if file is locked or signed, keep status at 3
  // Also set isVerified and isSigned to false when fields are updated
  if (hasFieldsUpdate) {
    file.isVerified = false;
    file.isSigned = false;
    if (file.isLocked === true || file.isSigned === true) {
      file.status = 3;
    } else {
      file.status = 1;
    }
  } else if (updates.isSigned === true) {
    // Side effect: when isSigned is set to true, set status to 4 (finalized)
    // Sign also locks the file
    file.isLocked = true;
    file.status = 4;
  } else if (updates.isVerified === true) {
    // Side effect: when isVerified is set to true, set status to 2
    file.status = 2;
  } else if (updates.isLocked === true) {
    // Side effect: when isLocked is set to true, set status to 3
    file.status = 3;
  } else if (updates.isLocked === false) {
    // Side effect: when isLocked is set to false, set status to 1
    file.status = 1;
  }
};

// Slice
const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearFolders: (state) => {
      state.folders = [];
      state.error = null;
      state.hasFetched = false;
    },
    replaceFoldersFromQuery: (state, action: PayloadAction<Folder[]>) => {
      state.folders = action.payload;
      state.error = null;
    },
    addFolder: (state, action: PayloadAction<Folder>) => {
      // Check if folder already exists
      const existingFolder = state.folders.find(
        (f) => f.id === action.payload.id
      );
      if (!existingFolder) {
        state.folders.push(action.payload);
      }
    },
    syncFromStorage: (state, action: PayloadAction<Folder[]>) => {
      // Update folders from another tab
      state.folders = action.payload;
    },
    updateFileFromWebSocket: (
      state,
      action: PayloadAction<{ fileId: string; fileData: any }>
    ) => {
      const { fileId, fileData } = action.payload;
      // Find the folder that contains this file
      for (const folder of state.folders) {
        const fileIndex = folder.files.findIndex((f) => f.id === fileId);
        if (fileIndex !== -1) {
          // Replace the entire file with the websocket data
          // This avoids deep assign issues that could cause duplicates
          console.log('fileData', fileData);
          folder.files[fileIndex] = fileData;
          // Update folder timestamp
          folder.updatedAt = new Date().toISOString();
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch folders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.folders = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch folders';
        state.hasFetched = true;
      });

    builder
      .addCase(fetchFolderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.folders = [];
        state.hasFetched = true;
      })
      .addCase(fetchFolderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.folders = action.payload ? [action.payload] : [];
        state.hasFetched = true;
      })
      .addCase(fetchFolderById.rejected, (state, action) => {
        state.isLoading = false;
        state.folders = [];
        state.error = action.error.message || 'Failed to fetch folder';
        state.hasFetched = false;
      });

    // Update folder
    builder
      .addCase(updateFolder.fulfilled, (state, action) => {
        const { folderId, updates } = action.payload;
        const folder = state.folders.find((f) => f.id === folderId);
        if (folder) {
          Object.assign(folder, updates);
          folder.updatedAt = new Date().toISOString();
        }
      })
      .addCase(updateFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update folder';
      });

    // Update folder file
    builder
      .addCase(updateFolderFile.fulfilled, (state, action) => {
        const { folderId, fileId, updates } = action.payload;
        const folderIndex = state.folders.findIndex((f) => f.id === folderId);
        if (folderIndex !== -1) {
          const folder = state.folders[folderIndex];
          const fileIndex = folder.files.findIndex((f) => f.id === fileId);

          if (fileIndex !== -1) {
            const file = folder.files[fileIndex];

            // Check if fields are being updated
            const hasFieldsUpdate =
              updates.fields && Array.isArray(updates.fields);

            // Apply updates with special handling for infinite-list fields
            if (hasFieldsUpdate) {
              // Process fields, handling infinite-list values with flags
              file.fields = updates.fields.map((updateField: any) => {
                const existingField = file.fields?.find(
                  (f: any) => f.name === updateField.name
                );

                // If this is an infinite-list field, process the values
                if (
                  updateField.type === 'infinite-list' &&
                  existingField &&
                  Array.isArray(existingField.value) &&
                  Array.isArray(updateField.value)
                ) {
                  return {
                    ...updateField,
                    value: processInfiniteListValue(
                      existingField.value,
                      updateField.value
                    ),
                  };
                }

                // For other field types, use the incoming value
                return updateField;
              });
            }

            // Apply other updates directly (Immer handles immutability automatically)
            Object.keys(updates).forEach((key) => {
              if (key !== 'fields') {
                (file as any)[key] = updates[key];
              }
            });

            // Apply side effects
            applySideEffects(file, updates, hasFieldsUpdate);

            file.updatedAt = new Date().toISOString();
          }

          state.folders[folderIndex].updatedAt = new Date().toISOString();
        }
      })
      .addCase(updateFolderFile.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update file';
      });

    // Delete folder file
    builder
      .addCase(deleteFolderFile.fulfilled, (state, action) => {
        const { folderId, fileId } = action.payload;
        const folder = state.folders.find((f) => f.id === folderId);
        if (folder) {
          folder.files = folder.files.filter((f) => f.id !== fileId);
          folder.updatedAt = new Date().toISOString();
        }
      })
      .addCase(deleteFolderFile.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete file';
      });

    // Duplicate folder file
    builder
      .addCase(duplicateFolderFile.fulfilled, (state, action) => {
        const { folderId, newFile } = action.payload;
        const folder = state.folders.find((f) => f.id === folderId);
        if (folder) {
          folder.files.push(newFile);
          folder.updatedAt = new Date().toISOString();
        }
      })
      .addCase(duplicateFolderFile.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to duplicate file';
      });

    // Add file to folder
    builder
      .addCase(addFileToFolder.fulfilled, (state, action) => {
        const { folderName, newFile } = action.payload;
        const folder = state.folders.find((f) => f.folderName === folderName);
        if (folder) {
          folder.files.push(newFile);
          folder.updatedAt = new Date().toISOString();
        }
      })
      .addCase(addFileToFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add file';
      });

    // Delete folder
    builder
      .addCase(deleteFolder.fulfilled, (state, action) => {
        const { folderId } = action.payload;
        state.folders = state.folders.filter((f) => f.id !== folderId);
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete folder';
      });

    // Send file email
    builder
      .addCase(sendFileEmail.fulfilled, (state, action) => {
        const { folderId, fileId, emailsSent } = action.payload;
        const folder = state.folders.find((f) => f.id === folderId);
        if (folder) {
          const file = folder.files.find((f) => f.id === fileId);
          if (file) {
            file.emailsSent = emailsSent;
            file.updatedAt = new Date().toISOString();
          }
          folder.updatedAt = new Date().toISOString();
        }
      })
      .addCase(sendFileEmail.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send email';
      });

    // Fetch and add folder
    builder
      .addCase(fetchAndAddFolder.fulfilled, (state, action) => {
        // Check if folder already exists
        const existingFolder = state.folders.find(
          (f) => f.id === action.payload.id
        );
        if (!existingFolder) {
          state.folders.push(action.payload);
        }
      })
      .addCase(fetchAndAddFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch folder';
      });

    // Fetch client folder
    builder
      .addCase(fetchClientFolder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClientFolder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Check if folder already exists, update it if it does
        const existingFolderIndex = state.folders.findIndex(
          (f) =>
            f.id === action.payload.id ||
            f.folderName === action.payload.folderName
        );
        if (existingFolderIndex !== -1) {
          state.folders[existingFolderIndex] = action.payload;
        } else {
          state.folders.push(action.payload);
        }
      })
      .addCase(fetchClientFolder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch folder';
      });

    // Update client file (optimistic update)
    builder
      .addCase(updateClientFile.pending, (state, action) => {
        // Optimistic update: apply changes immediately
        const { folderId, fileId, updates } = action.meta.arg;
        const folderIndex = state.folders.findIndex(
          (f) => f.id === folderId || f.folderName === folderId
        );
        if (folderIndex !== -1) {
          const folder = state.folders[folderIndex];
          const fileIndex = folder.files.findIndex((f) => f.id === fileId);

          if (fileIndex !== -1) {
            const file = folder.files[fileIndex];

            // Check if fields are being updated
            const hasFieldsUpdate =
              updates.fields && Array.isArray(updates.fields);

            // Apply updates optimistically with special handling for infinite-list fields
            if (hasFieldsUpdate) {
              // Process fields, handling infinite-list values with flags
              file.fields = updates.fields.map((updateField: any) => {
                const existingField = file.fields?.find(
                  (f: any) => f.name === updateField.name
                );

                // If this is an infinite-list field, process the values
                if (
                  updateField.type === 'infinite-list' &&
                  existingField &&
                  Array.isArray(existingField.value) &&
                  Array.isArray(updateField.value)
                ) {
                  return {
                    ...updateField,
                    value: processInfiniteListValue(
                      existingField.value,
                      updateField.value
                    ),
                  };
                }

                // For other field types, use the incoming value
                return updateField;
              });
            }

            // Apply other updates directly
            Object.keys(updates).forEach((key) => {
              if (key !== 'fields') {
                (file as any)[key] = updates[key];
              }
            });

            // Apply side effects
            applySideEffects(file, updates, hasFieldsUpdate);

            file.updatedAt = new Date().toISOString();
            folder.updatedAt = new Date().toISOString();
          }
        }
      })
      .addCase(updateClientFile.fulfilled, (state, action) => {
        // Update is already applied optimistically, just ensure consistency
        const { folderId, fileId, updates } = action.payload;
        const folderIndex = state.folders.findIndex(
          (f) => f.id === folderId || f.folderName === folderId
        );
        if (folderIndex !== -1) {
          const folder = state.folders[folderIndex];
          const fileIndex = folder.files.findIndex((f) => f.id === fileId);

          if (fileIndex !== -1) {
            const file = folder.files[fileIndex];

            // Check if fields are being updated
            const hasFieldsUpdate =
              updates.fields && Array.isArray(updates.fields);

            // Apply updates with special handling for infinite-list fields
            if (hasFieldsUpdate) {
              // Process fields, handling infinite-list values with flags
              file.fields = updates.fields.map((updateField: any) => {
                const existingField = file.fields?.find(
                  (f: any) => f.name === updateField.name
                );

                // If this is an infinite-list field, process the values
                if (
                  updateField.type === 'infinite-list' &&
                  existingField &&
                  Array.isArray(existingField.value) &&
                  Array.isArray(updateField.value)
                ) {
                  return {
                    ...updateField,
                    value: processInfiniteListValue(
                      existingField.value,
                      updateField.value
                    ),
                  };
                }

                // For other field types, use the incoming value
                return updateField;
              });
            }

            // Apply other updates directly
            Object.keys(updates).forEach((key) => {
              if (key !== 'fields') {
                (file as any)[key] = updates[key];
              }
            });

            // Apply side effects
            applySideEffects(file, updates, hasFieldsUpdate);

            file.updatedAt = new Date().toISOString();
            folder.updatedAt = new Date().toISOString();
          }
        }
      })
      .addCase(updateClientFile.rejected, (state, action) => {
        // Revert optimistic update on error
        // Note: In a production app, you'd want to store the previous state
        // and revert to it. For now, we'll just set an error.
        state.error = action.error.message || 'Failed to update file';
      });
  },
});

export const {
  clearError,
  clearFolders,
  addFolder,
  syncFromStorage,
  updateFileFromWebSocket,
  replaceFoldersFromQuery,
} = foldersSlice.actions;
export default foldersSlice.reducer;
